import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button, ButtonVariant } from "@src/components/button/Button";
import EventContextHeader, { TContextListing } from "@src/components/eventContextHeader/EventContextHeader";
import { Loader } from "@src/components/loader/Loader";
import { CommonModal } from "@src/components/modal/CommonModal";
import Checkbox from "@src/components/vGrid/Checkbox";
import { VGrid, VGridRef } from "@src/components/vGrid/VGrid";
import { DateFormatsEnum, adjustDate, formatDate } from "@src/components/utils/dateUtils";
import { pricingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { duplicateListingEventsSlice } from "@src/store/pricing/duplicateListingEventsSlice";
import { invalidateEventInventoryCache } from "@src/store/pricing/queryEventInventoryAllSlice";
import { EventRecord, IQueryEventParams } from "@src/store/pricing/queryEventsSlice";
import { EventColumnType } from "@src/store/pricing/settingsSlice";
import { convertToLocalTime } from "@src/utils/convertToLocaltime";
import { DuplicateFilters, DuplicateFilterValues } from "./DuplicateFilters";
import styles from "./DuplicateListing.module.scss";

interface DuplicateListingProps {
	listingIds: Array<number>;
	headerListings?: Array<TContextListing>;
	hasSoldListings?: boolean;
	closeModal: () => void;
	onSuccess?: () => void;
}

const IGNORED = "***IGNORED***";

const formatIds = (ids: Array<number>, max = 10): string =>
	ids.length <= max ? ids.join(", ") : `${ids.slice(0, max).join(", ")}, +${ids.length - max} more`;

export const DuplicateListing: React.FC<DuplicateListingProps> = ({
	listingIds,
	headerListings,
	hasSoldListings = false,
	closeModal,
	onSuccess,
}) => {
	const dispatch = useAppDispatch();
	const vGridRef = useRef<VGridRef>(null);

	const companyName = useAppSelector((state) => state.settings.companyName);
	const localTime = useAppSelector((state) => state.settings.isLocalTime);
	const columnsEvent = useAppSelector((state) => state.pricing.settings.columns.Event);
	const dupEvents = useAppSelector((state) => state.pricing.duplicateListingEvents);
	const duplicateLoading = useAppSelector((state) => state.pricing.duplicateListings.loading);

	const [selectedEvents, setSelectedEvents] = useState<Array<EventRecord>>([]);
	const [removeOldListings, setRemoveOldListings] = useState(false);
	const [hasFiltered, setHasFiltered] = useState(false);

	useEffect(() => {
		dispatch(duplicateListingEventsSlice.actions.reset());
	}, [dispatch]);

	const eventData = useMemo(
		() => dupEvents.data.Events.filter((event) => event.Event_Name !== IGNORED),
		[dupEvents.data.Events],
	);

	const columnsWithLocalTime = useMemo<Array<EventColumnType>>(() => {
		return columnsEvent.map((column) => {
			if (!["Day", "Date", "Time"].includes(column.id)) return column;

			return {
				...column,
				formatValue: (_value: unknown, { rowData }: { rowData: EventRecord }) => {
					const { Event_Date, Event_Time, Venue_State } = rowData;
					const getLocalTime = () => convertToLocalTime(Event_Date, Event_Time, Venue_State);

					switch (column.id) {
						case "Day":
							return localTime ? getLocalTime().toFormat("cccc") : formatDate(Event_Date, DateFormatsEnum.dddd);

						case "Date":
							return localTime
								? getLocalTime().toFormat("MM/dd/yyyy")
								: formatDate(Event_Date, DateFormatsEnum["MM/DD/YYYY"]);

						case "Time":
							if (Event_Time === "TBA") return "TBA";
							return localTime ? getLocalTime().toFormat("hh:mm a") : Event_Time;

						default:
							return "";
					}
				},
			};
		});
	}, [columnsEvent, localTime]);

	const handleFilter = (values: DuplicateFilterValues) => {
		const params: IQueryEventParams & { skipCache?: boolean; skipSavingCache?: boolean } = {
			CompanyName: companyName || null,
			ForceRefresh: true,
			ShowSold: true,
			IncludeParking: values.includeParking,
			SearchString: values.search || null,
			EventFromDate: values.startDate ? adjustDate(values.startDate, 0) : (adjustDate(new Date(), 0) ?? null),
			EventToDate: values.endDate ? adjustDate(values.endDate, 0) : null,
			EventId: values.eventId ? Number(values.eventId) : null,
			CategoryId: values.categoryId ? Number(values.categoryId) : null,
			EventHeadlinerId: values.headlinerId ? Number(values.headlinerId) : null,
			VenueId: values.venueId ? Number(values.venueId) : null,
			OrderByColumn: [{ property: "Event_Date", direction: "ASC" }],
			StartIndex: 0,
			Limit: 9999,
			skipCache: true,
			skipSavingCache: true,
		};

		setSelectedEvents([]);
		vGridRef.current?.clearSelection();
		setHasFiltered(true);
		dispatch(pricingActions.queryDuplicateListingEvents(params));
	};

	const handleSave = async () => {
		if (!listingIds.length || !selectedEvents.length) return;

		const eventIds = selectedEvents.map((event) => event.Event_ID);
		const listingPart = `${listingIds.length > 1 ? "Listings" : "Listing"} ${formatIds(listingIds)}`;
		const eventPart = `${eventIds.length > 1 ? "events" : "event"} ${formatIds(eventIds)}`;
		const description = `${listingPart} → ${eventPart}`;

		try {
			await dispatch(
				pricingActions.duplicateListings({
					listingIds,
					eventIds,
					removeOldListings: removeOldListings && !hasSoldListings,
				}),
			).unwrap();
			invalidateEventInventoryCache(eventIds);
			toast.success(`${listingIds.length > 1 ? "Listings" : "Listing"} duplicated`, { description });
			closeModal();
			onSuccess?.();
		} catch {
			toast.error("Duplicate failed", { description });
		}
	};

	const renderTable = () => {
		if (dupEvents.loading)
			return (
				<div className={styles.tablePlaceholder}>
					<Loader />
				</div>
			);
		if (!hasFiltered) return <div className={styles.tablePlaceholder}>Use the filters to find events.</div>;
		if (!eventData.length) return <div className={styles.tablePlaceholder}>{dupEvents.error ?? "No events found."}</div>;

		return (
			<VGrid<EventRecord>
				ref={vGridRef}
				tableId="duplicate-listing-events"
				rowSelection="multiple"
				columns={columnsWithLocalTime}
				rowsData={eventData}
				getRowNodeId={(x) => x.Event_ID}
				onSelectionChange={(rows) => setSelectedEvents(rows)}
				wrapperStyle={{ padding: 0 }}
			/>
		);
	};

	return (
		<CommonModal
			width="100%"
			padding={0}
			title="Duplicate Listing"
			closeModal={closeModal}
			content={
				<div className={styles.body}>
					<div className={styles.contextHeader}>
						<EventContextHeader listings={headerListings} padding />
					</div>
					<div className={styles.root}>
						<aside className={styles.sidebar}>
							<DuplicateFilters onFilter={handleFilter} loading={dupEvents.loading} />
						</aside>
						<main className={styles.tableArea}>
							<div className={styles.tableWrap}>{renderTable()}</div>
							<div className={styles.tableFooter}>
								<span>Total Events: {eventData.length.toLocaleString()}</span>
								<span className={styles.divider}>|</span>
								<span>Events Selected: {selectedEvents.length.toLocaleString()}</span>
							</div>
						</main>
					</div>
				</div>
			}
			buttons={
				<div className={styles.footer}>
					<label className={styles.removeOld}>
						<span>Remove old listings:</span>
						<Checkbox
							isChecked={removeOldListings && !hasSoldListings}
							onChange={(checked) => setRemoveOldListings(checked)}
							isDisabled={hasSoldListings}
							controlSize="m"
						/>
					</label>
					<Button
						onClick={closeModal}
						disabled={duplicateLoading}
						variant={ButtonVariant.Secondary}
						controlSize="sm">
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						disabled={duplicateLoading || !selectedEvents.length}
						isLoading={duplicateLoading}
						variant={ButtonVariant.Primary}
						controlSize="sm">
						Save
					</Button>
				</div>
			}
		/>
	);
};

export default DuplicateListing;
